<template>
  <v-container fluid class="ev-modules-page" @click="closeMenus()">
    <!-- Header card -->
    <div class="ev-modules-head">
      <button
        type="button"
        class="ev-modules-head__back"
        title="Back"
        @click="goBack()"
      >
        <v-icon size="18" :color="isDark ? 'var(--app-neutral-fg)' : '#475569'">
          mdi-arrow-left
        </v-icon>
      </button>

      <div class="ev-modules-head__info">
        <h1 class="ev-modules-head__title">Event Modules</h1>
        <span class="ev-modules-head__subtitle">
          Modules registered across the HRDC event services
        </span>
      </div>

      <div class="ev-modules-head__stats">
        <span class="ev-modules-stat">
          <span class="ev-modules-stat__value">{{ activeCount }}</span>
          <span class="ev-modules-stat__label">Active</span>
        </span>
        <span class="ev-modules-stat__sep"></span>
        <span class="ev-modules-stat">
          <span class="ev-modules-stat__value">{{ modules.length }}</span>
          <span class="ev-modules-stat__label">Total</span>
        </span>
      </div>

      <button type="button" class="ev-new-btn" @click="openCreate()">
        <v-icon size="17" color="#fff">mdi-plus</v-icon>
        New Module
      </button>
    </div>

    <!-- Content card: toolbar sits on the card head, the grid on the inner plate -->
    <div class="ev-modules-card">
      <!-- Toolbar: search / filter / sort -->
      <div class="ev-toolbar">
        <div class="ev-search">
          <v-icon size="17" color="var(--app-text-faint)">mdi-magnify</v-icon>
          <input
            type="text"
            class="ev-search__input"
            placeholder="Search modules..."
            v-model="search"
          />
        </div>

        <div class="ev-pills">
          <button
            type="button"
            class="ev-pill"
            :class="{ 'ev-pill--active': filter === 'all' }"
            @click="filter = 'all'"
          >All</button>
          <button
            type="button"
            class="ev-pill"
            :class="{ 'ev-pill--active': filter === 'active' }"
            @click="filter = 'active'"
          >Active</button>
          <button
            type="button"
            class="ev-pill"
            :class="{ 'ev-pill--active': filter === 'inactive' }"
            @click="filter = 'inactive'"
          >Inactive</button>
        </div>

        <div class="ev-sort">
          <button type="button" class="ev-sort__btn" @click.stop="sortMenuOpen = !sortMenuOpen">
            <v-icon size="15" color="var(--app-text-faint)">mdi-sort</v-icon>
            Sort: {{ sortLabels[sort] }}
            <v-icon size="15" color="var(--app-text-faint)">mdi-chevron-down</v-icon>
          </button>
          <div v-if="sortMenuOpen" class="ev-sort__menu">
            <button
              type="button"
              class="ev-sort__item"
              :class="{ 'ev-sort__item--active': sort === 'name' }"
              @click="setSort('name')"
            >Name</button>
            <button
              type="button"
              class="ev-sort__item"
              :class="{ 'ev-sort__item--active': sort === 'type' }"
              @click="setSort('type')"
            >HRDC Type</button>
            <button
              type="button"
              class="ev-sort__item"
              :class="{ 'ev-sort__item--active': sort === 'price' }"
              @click="setSort('price')"
            >Price</button>
          </div>
        </div>
      </div>

      <div class="ev-modules-card__body">
        <!-- Loading -->
        <div v-if="loading" class="ev-loading">
          <v-progress-circular indeterminate color="var(--app-accent)" size="32"></v-progress-circular>
          <span class="ev-loading__text">Loading event modules...</span>
        </div>

        <!-- Module Cards -->
        <div v-else-if="visibleModules.length" class="ev-modules-grid">
          <div
            v-for="mod in visibleModules"
            :key="mod.uuid"
            class="ev-module-card"
            :class="{ 'ev-module-card--inactive': !mod.isActive }"
          >
            <div class="ev-module-card__stripe" :style="{ background: stripeColor(mod) }"></div>

            <div class="ev-module-card__inner">
              <!-- Top: Icon + Title + Status -->
              <div class="ev-module-card__top">
                <div class="ev-module-card__icon" :style="{ background: iconBg(mod), color: accentColor(mod) }">
                  <v-icon :color="accentColor(mod)" size="21">{{ mod.icon || 'mdi-puzzle-outline' }}</v-icon>
                </div>
                <div class="ev-module-card__info">
                  <h3 class="ev-module-card__title">{{ mod.appName }}</h3>
                  <span class="ev-module-card__key">{{ mod.serviceKey }} &middot; ID {{ mod.hrdcType }}</span>
                </div>
                <span
                  class="ev-module-card__status"
                  :style="{ color: mod.isActive ? okColor : mutedColor }"
                >
                  <span
                    class="ev-module-card__status-dot"
                    :style="{ background: mod.isActive ? okColor : mutedColor }"
                  ></span>
                  {{ mod.isActive ? 'Active' : 'Inactive' }}
                </span>
              </div>

              <!-- Pricing -->
              <div class="ev-module-card__pricing" v-if="hasPricing(mod)">
                <div class="ev-module-card__price-row" v-if="memberRange(mod)">
                  <span class="ev-module-card__price-label">Member</span>
                  <span class="ev-module-card__price-value" :style="{ color: accentColor(mod) }">
                    {{ memberRange(mod) }}
                  </span>
                </div>
                <div class="ev-module-card__price-row" v-if="nonMemberRange(mod)">
                  <span class="ev-module-card__price-label">Non-Member</span>
                  <span class="ev-module-card__price-value ev-module-card__price-value--muted">
                    {{ nonMemberRange(mod) }}
                  </span>
                </div>
              </div>

              <!-- Actions -->
              <div class="ev-module-card__actions">
                <button class="ev-module-card__action-btn" @click="editModule(mod)">
                  <v-icon size="15" :color="isDark ? 'var(--app-neutral-fg)' : '#475569'">mdi-pencil-outline</v-icon> Edit
                </button>
                <button
                  v-if="mod.isActive"
                  class="ev-module-card__action-btn ev-module-card__action-btn--danger"
                  @click="deactivateModule(mod)"
                >
                  <v-icon size="14" :color="dangerColor">mdi-power</v-icon> Deactivate
                </button>
                <button
                  v-else
                  class="ev-module-card__action-btn ev-module-card__action-btn--activate"
                  @click="activateModule(mod)"
                >
                  <v-icon size="14" :color="okColor">mdi-power</v-icon> Activate
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty / no results -->
        <div v-else class="ev-empty">
          <v-icon size="34" color="var(--app-text-faint)">mdi-file-search-outline</v-icon>
          <div class="ev-empty__text">No modules match your search or filter.</div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Dialog -->
    <v-dialog
      v-model="showAddForm"
      max-width="80vw"
      width="80vw"
      content-class="ev-dialog"
      persistent
    >
      <EventModuleForm
        v-if="showAddForm"
        :editData="editData"
        :serviceUrl="serviceUrl"
        :nextHrdcType="modules.length + 1"
        :modules="modules"
        @saved="onModuleSaved"
        @close="closeForm"
        @message="onFormMessage"
      />
    </v-dialog>

    <!-- Activate / Deactivate Confirm -->
    <v-dialog v-model="showStatusConfirm" max-width="380" content-class="confirm-dialog" :persistent="statusSubmitting">
      <div class="confirm-card">
        <h3 class="confirm-card__title">
          {{ isActivating ? 'Activate Module' : 'Deactivate Module' }}
        </h3>
        <p class="confirm-card__message" v-if="isActivating">
          Are you sure you want to activate <strong>{{ statusTargetName }}</strong>?
          This module will become available to users after page reload.
        </p>
        <p class="confirm-card__message" v-else>
          Are you sure you want to deactivate <strong>{{ statusTargetName }}</strong>?
          This module will no longer be available to users after page reload.
        </p>
        <div class="confirm-card__actions">
          <button
            class="confirm-card__btn confirm-card__btn--cancel"
            :disabled="statusSubmitting"
            @click="showStatusConfirm = false"
          >Cancel</button>
          <button
            class="confirm-card__btn"
            :class="isActivating ? 'confirm-card__btn--activate' : 'confirm-card__btn--danger'"
            :disabled="statusSubmitting"
            @click="confirmStatusChange()"
          >{{ isActivating ? 'Activate' : 'Deactivate' }}</button>
        </div>
      </div>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import EventModuleForm from "./Form.vue";
import { themeState } from "@/plugins/theme";
import { darkAccent } from "@/objects/accentColor";

export default {
  mixins: [hrdcFunction],
  components: { EventModuleForm },
  computed: {
    // Reactive global theme flag — the accent helpers read it during render, so
    // the cards re-tint the moment the navbar toggle flips.
    isDark() {
      return themeState.dark;
    },
    // The status/action hues are inline too, and the light values are all on the
    // dark side of the ramp (#059669 ≈ 2.6:1, #475569 ≈ 1.9:1 on --app-card).
    // Dark mode uses the status tokens, which are the same hues one step bright.
    okColor() {
      return this.isDark ? "var(--app-success-fg)" : "#059669";
    },
    dangerColor() {
      return this.isDark ? "var(--app-danger-fg)" : "#ef4444";
    },
    mutedColor() {
      return this.isDark ? "var(--app-text-faint)" : "#64748b";
    },
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    activeCount() {
      return this.modules.filter((mod) => mod.isActive).length;
    },
    isActivating() {
      return this.statusMode === "activate";
    },
    statusTargetName() {
      return this.statusTarget ? this.statusTarget.appName : "";
    },
    // Search + filter + sort are purely client-side view state; the loaded
    // module list itself is never mutated here.
    visibleModules() {
      const query = this.search.trim().toLowerCase();

      const list = this.modules.filter((mod) => {
        if (this.filter === "active" && !mod.isActive) return false;
        if (this.filter === "inactive" && mod.isActive) return false;
        if (query) {
          const appName = (mod.appName || "").toLowerCase();
          const serviceKey = (mod.serviceKey || "").toLowerCase();
          if (!appName.includes(query) && !serviceKey.includes(query)) return false;
        }
        return true;
      });

      return list.sort((a, b) => {
        // Inactive modules always sink to the bottom of the grid.
        if (!!a.isActive !== !!b.isActive) return a.isActive ? -1 : 1;
        if (this.sort === "name") {
          return (a.appName || "").localeCompare(b.appName || "");
        }
        if (this.sort === "type") {
          return (a.hrdcType || 0) - (b.hrdcType || 0);
        }
        if (this.sort === "price") {
          return this.lowestPrice(a) - this.lowestPrice(b);
        }
        return 0;
      });
    },
  },
  props: ["params"],
  data: () => ({
    model: new Model(),
    service: new Service(),
    modules: [],
    loading: true,
    showAddForm: false,
    editData: null,
    // Shared confirm dialog for both Activate and Deactivate.
    showStatusConfirm: false,
    statusMode: "deactivate",
    statusTarget: null,
    statusSubmitting: false,
    serviceUrl: null,
    search: "",
    filter: "all",
    sort: "name",
    sortMenuOpen: false,
    sortLabels: { name: "Name", type: "HRDC Type", price: "Price" },
  }),
  async created() {
    if (this.params && this.params.modelKey) {
      this.model.getByKey(this.params.modelKey);
      if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
    }

    // Page is shared across all events — always resolve to first event module's service key
    if (!this.$service[this.service.key] || this.$route.params.serviceKey === "hrdc_event_settings") {
      const { getAllEventConfigs } = require("@/services/hrdc_event/eventRegistry");
      const firstConfig = getAllEventConfigs()[0];
      if (firstConfig) {
        this.service.set("key", firstConfig.serviceKey);
      }
    }

    this.serviceUrl = this.$service[this.service.key];
    await this.loadModules();
  },
  methods: {
    goBack() {
      const from = this.$route.query.from;
      if (from) {
        this.$router.push({
          name: "PageServiceDashboard",
          params: { serviceKey: from },
        }).catch(() => {});
        return;
      }
      if (this.$route.params.serviceKey === "hrdc_event_settings") {
        this.$router.push({
          name: "PageServiceDashboard",
          params: { serviceKey: "hrdc_event_settings" },
        }).catch(() => {});
      } else {
        this.$router.push({ name: this.eventRoute("Settings") }).catch(() => {});
      }
    },
    setSort(value) {
      this.sort = value;
      this.sortMenuOpen = false;
    },
    closeMenus() {
      this.sortMenuOpen = false;
    },
    async loadModules() {
      this.loading = true;
      try {
        const url = `${this.$service[this.service.key]}/v1/en/console/event_module/list`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${this.auth.token}` },
        });
        const result = await response.json();
        if (result.status) {
          this.modules = result.data;
        }
      } catch (err) {
        console.error("Failed to load event modules:", err);
      } finally {
        this.loading = false;
      }
    },
    editModule(mod) {
      this.editData = { ...mod };
      this.showAddForm = true;
    },
    openCreate() {
      // Clear any stale editData before opening the Create dialog so the
      // form renders in "New Module" mode even if the previous Edit dialog
      // was dismissed by clicking outside / pressing ESC (those paths
      // don't fire @close, so closeForm() never ran to reset editData).
      this.editData = null;
      this.showAddForm = true;
    },
    closeForm() {
      this.showAddForm = false;
      this.editData = null;
    },
    async onModuleSaved() {
      this.closeForm();
      await this.loadModules();
      this.$store.dispatch("showMessage", {
        message: "Event module saved. Reload the page to activate changes.",
        messageType: "success",
        timeout: 4000,
      });
    },
    deactivateModule(mod) {
      this.statusMode = "deactivate";
      this.statusTarget = mod;
      this.showStatusConfirm = true;
    },
    activateModule(mod) {
      this.statusMode = "activate";
      this.statusTarget = mod;
      this.showStatusConfirm = true;
    },
    async confirmStatusChange() {
      if (!this.statusTarget || this.statusSubmitting) return;
      this.statusSubmitting = true;
      const activating = this.statusMode === "activate";
      try {
        if (activating) {
          const url = `${this.$service[this.service.key]}/v1/en/console/event_module/update`;
          await fetch(url, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.auth.token}`,
            },
            body: JSON.stringify({ uuid: this.statusTarget.uuid, isActive: true }),
          });
        } else {
          const url = `${this.$service[this.service.key]}/v1/en/console/event_module/deactivate/${this.statusTarget.uuid}`;
          await fetch(url, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${this.auth.token}` },
          });
        }
        this.showStatusConfirm = false;
        this.statusTarget = null;
        await this.loadModules();
        this.$store.dispatch("showMessage", {
          message: activating
            ? "Module activated. Reload to apply changes."
            : "Module deactivated. Reload to apply changes.",
          messageType: "success",
          timeout: 3000,
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.statusSubmitting = false;
      }
    },
    onFormMessage(msg) {
      this.$store.dispatch("showMessage", {
        message: msg.text,
        messageType: msg.type,
        timeout: 3000,
      });
    },
    hasPricing(mod) {
      return !!(this.memberRange(mod) || this.nonMemberRange(mod));
    },
    memberRange(mod) {
      return this.priceRange(this.getMemberPricing(mod));
    },
    nonMemberRange(mod) {
      return this.priceRange(this.getNonMemberPricing(mod));
    },
    // "RM 800" for a single price, "RM 800 – 1,200" once there are several.
    priceRange(prices) {
      if (!prices || !prices.length) return null;
      const nums = [...prices].sort((a, b) => a - b);
      if (nums.length === 1) return `RM ${nums[0].toLocaleString()}`;
      return `RM ${nums[0].toLocaleString()} – ${nums[nums.length - 1].toLocaleString()}`;
    },
    lowestPrice(mod) {
      const all = [...this.getMemberPricing(mod), ...this.getNonMemberPricing(mod)];
      return all.length ? Math.min(...all) : Number.MAX_SAFE_INTEGER;
    },
    getMemberPricing(mod) {
      if (!mod.staticPricing) return [];
      if (typeof mod.staticPricing === "object" && !Array.isArray(mod.staticPricing)) {
        return mod.staticPricing.member || [];
      }
      // Legacy flat array - treat all as member
      return Array.isArray(mod.staticPricing) ? mod.staticPricing : [];
    },
    getNonMemberPricing(mod) {
      if (!mod.staticPricing) return [];
      if (typeof mod.staticPricing === "object" && !Array.isArray(mod.staticPricing)) {
        return mod.staticPricing.nonMember || [];
      }
      return [];
    },
    rawAccent(mod) {
      return mod.accentColor || this.getModuleColor(mod.hrdcType);
    },
    // Inactive cards drop their accent so the whole card reads as muted.
    //
    // Accents are picked in the Form beside a white preview, so the deeper ones
    // (Sales Tax's #1e3a5f = 1.1:1 on --app-card) become an invisible glyph, an
    // invisible stripe and an unreadable member price once the page goes dark.
    // darkAccent() keeps the hue and lifts only what falls below 5:1, so bright
    // modules look exactly as they do today. The muted greys are light-theme
    // values too — #e2e8f0 painted a near-white block on charcoal — so the
    // inactive branch switches to the neutral tokens.
    accentColor(mod) {
      if (!mod.isActive) {
        return this.isDark ? "var(--app-text-faint)" : "#94a3b8";
      }
      const accent = this.rawAccent(mod);
      return this.isDark ? darkAccent(accent) : accent;
    },
    stripeColor(mod) {
      if (!mod.isActive) {
        return this.isDark ? "var(--app-border-strong)" : "#cbd5e1";
      }
      const accent = this.rawAccent(mod);
      return this.isDark ? darkAccent(accent) : accent;
    },
    iconBg(mod) {
      // In dark mode the tile is rebuilt from currentColor by the .dark-mode
      // rules below — an 'accent + 17' hex (9% alpha) is tuned for white and
      // cannot express the token used for inactive cards anyway.
      if (this.isDark) return "transparent";
      if (!mod.isActive) return "#e2e8f0";
      return this.rawAccent(mod) + "17";
    },
    getModuleColor(hrdcType) {
      const colors = { 1: "#0ea5e9", 2: "#8b5cf6", 3: "#f59e0b", 4: "#10b981" };
      return colors[hrdcType] || "#64748b";
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .ev-modules-page {
  background-color: var(--app-page);
}

.dark-mode .ev-search {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .ev-search__input {
  color: var(--app-text);
}

.dark-mode .ev-search__input::placeholder {
  color: var(--app-text-faint);
}

.dark-mode .ev-pills {
  background: var(--app-elevated);
}

.dark-mode .ev-pill {
  color: var(--app-text-muted);
}

.dark-mode .ev-pill--active {
  background: var(--app-card);
  color: var(--app-text);
}

.dark-mode .ev-sort__btn {
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-neutral-fg);
}

.dark-mode .ev-sort__menu {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .ev-sort__item {
  color: var(--app-neutral-fg);
}

.dark-mode .ev-sort__item:hover {
  background: var(--app-surface);
}

.dark-mode .ev-sort__item--active {
  background: var(--app-elevated);
  color: var(--app-text);
}

.dark-mode .ev-loading__text {
  color: var(--app-text-muted);
}

.dark-mode .ev-empty {
  color: var(--app-text-faint);
}

.dark-mode .ev-module-card {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .ev-module-card--inactive {
  background: var(--app-elevated);
  border-color: var(--app-border);
}

.dark-mode .ev-module-card__title {
  color: var(--app-text);
}

.dark-mode .ev-module-card--inactive .ev-module-card__title {
  color: var(--app-text-muted);
}

.dark-mode .ev-module-card__key {
  color: var(--app-text-faint);
}

.dark-mode .ev-module-card--inactive .ev-module-card__key {
  color: var(--app-text-faint);
}

.dark-mode .ev-module-card__pricing {
  border-top: 1px solid var(--app-border);
}

.dark-mode .ev-module-card--inactive .ev-module-card__pricing {
  border-top-color: var(--app-border);
}

.dark-mode .ev-module-card__price-label {
  color: var(--app-text-faint);
}

.dark-mode .ev-module-card__price-value--muted {
  color: var(--app-neutral-fg);
}

.dark-mode .ev-module-card__actions {
  border-top: 1px solid var(--app-border);
}

.dark-mode .ev-module-card--inactive .ev-module-card__actions {
  border-top-color: var(--app-border);
}

.dark-mode .ev-module-card__action-btn {
  color: var(--app-neutral-fg);
}

/* >>> end auto-dark <<< */

/* The icon tile is written inline as accent + '17' (9% alpha) — a wash tuned
   against white that all but disappears on charcoal, leaving six identical grey
   squares. currentColor on the tile IS the accent (already lifted for dark mode
   by accentColor → darkAccent), so the tile is rebuilt from it here: ::before is
   the wash, ::after the ring that gives it an edge. Two pseudo-elements because
   `opacity` applies to the whole element — a ring drawn on the wash would be
   diluted to the wash's own alpha and vanish. Matches the service cards on
   PageConsoleDashboard. */
.dark-mode .ev-module-card__icon {
  background: transparent !important;
  position: relative;
}

.dark-mode .ev-module-card__icon::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0.16;
}

.dark-mode .ev-module-card__icon::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px currentColor;
  opacity: 0.3;
}

.dark-mode .ev-module-card__icon .v-icon {
  position: relative;
}

/* ============================================================
   Dark mode — page shell
   The tiers step page (#22262d) < surface (#272c34) < card (#2c313a), so the
   inner plate reads as a well inside the card and the module cards sitting on
   it stay a step brighter than their ground — the same relationship the light
   palette has (#d8dce2 / #f8fafc / #fff).
   ============================================================ */
.dark-mode .ev-modules-head,
.dark-mode .ev-modules-card {
  background: var(--app-card);
  box-shadow: 0 1px 4px var(--app-shadow-soft);
}

.dark-mode .ev-modules-head__back {
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .ev-modules-head__back:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .ev-modules-head__title {
  color: var(--app-text);
}

.dark-mode .ev-modules-head__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .ev-modules-stat__value {
  color: var(--app-text);
}

.dark-mode .ev-modules-stat__label {
  color: var(--app-text-faint);
}

.dark-mode .ev-modules-stat__sep {
  background: var(--app-border);
}

.dark-mode .ev-toolbar {
  border-bottom-color: var(--app-border);
}

.dark-mode .ev-modules-card__body {
  background: var(--app-surface);
}

.ev-modules-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; the old `min-height: 100vh`
     only accounted for the app bar and overflowed. See .layout-main__content
     in LayoutConsoleV2.vue. */
  flex: 1 0 auto;
  padding: 36px 44px 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================================
   Header card — back / title / counts / primary action
   ============================================================ */
.ev-modules-head {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.03);
  padding: 18px 22px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.ev-modules-head__back {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.ev-modules-head__back:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
  transform: translateX(-2px);
}

.ev-modules-head__info {
  flex: 1;
  min-width: 0;
}

.ev-modules-head__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -0.3px;
  line-height: 1.3;
}

.ev-modules-head__subtitle {
  display: block;
  margin-top: 2px;
  font-size: 0.78rem;
  font-weight: 500;
  color: #94a3b8;
}

.ev-modules-head__stats {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}

.ev-modules-stat {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.ev-modules-stat__value {
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
}

.ev-modules-stat__label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
}

.ev-modules-stat__sep {
  width: 1px;
  height: 26px;
  background: #e2e8f0;
}

/* ============================================================
   Content card — toolbar head + module plate
   ============================================================ */
.ev-modules-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.03);
  /* NOT overflow:hidden — the sort dropdown is absolutely positioned inside
     the toolbar and would be clipped by the card. */
}

.ev-modules-card__body {
  padding: 20px;
  background: #f8fafc;
  border-radius: 0 0 16px 16px;
}

.ev-new-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 20px;
  border-radius: 10px;
  border: none;
  background: #0ea5e9;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 165, 233, 0.28);
  flex-shrink: 0;
  transition: background 0.15s ease;
}

.ev-new-btn:hover {
  background: #0284c7;
}

/* ============================================================
   Toolbar — the head of the content card
   ============================================================ */
.ev-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 20px;
  border-bottom: 1px solid #eef2f7;
}

/* The field sits on the white card head, so it takes the surface tier to stay
   visible — #fff on #fff read as a borderless void. */
.ev-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px 14px;
  min-width: 240px;
  max-width: 320px;
  flex: 1;
}

.ev-search__input {
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  color: #0f172a;
  flex: 1;
  min-width: 0;
}

.ev-search__input::placeholder {
  color: #94a3b8;
}

.ev-pills {
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}

.ev-pill {
  padding: 7px 15px;
  border-radius: 7px;
  border: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  background: transparent;
  color: #64748b;
  transition: all 0.15s ease;
}

.ev-pill--active {
  background: #fff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1);
}

.ev-sort {
  position: relative;
}

.ev-sort__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
}

.ev-sort__menu {
  position: absolute;
  top: 42px;
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  min-width: 160px;
  padding: 6px;
  z-index: 20;
}

.ev-sort__item {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  padding: 8px 10px;
  border-radius: 7px;
  cursor: pointer;
}

.ev-sort__item:hover {
  background: #f8fafc;
}

.ev-sort__item--active {
  background: #f1f5f9;
  font-weight: 700;
  color: #0f172a;
}

/* ============================================================
   States
   ============================================================ */
.ev-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 40px;
  justify-content: center;
}

.ev-loading__text {
  font-size: 0.85rem;
  color: #64748b;
}

.ev-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 60px 20px;
  color: #94a3b8;
}

.ev-empty__text {
  font-size: 14px;
  font-weight: 600;
}

/* ============================================================
   Cards
   ============================================================ */
.ev-modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.ev-module-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.ev-module-card:hover {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.09);
  transform: translateY(-2px);
}

.ev-module-card--inactive {
  background: #f1f5f9;
  border-color: #e2e8f0;
}

.ev-module-card__stripe {
  height: 4px;
  width: 100%;
}

.ev-module-card__inner {
  padding: 20px 20px 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Top: icon + info + status */
.ev-module-card__top {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  margin-bottom: 14px;
}

.ev-module-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ev-module-card__info {
  flex: 1;
  min-width: 0;
}

.ev-module-card__title {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.3;
}

.ev-module-card--inactive .ev-module-card__title {
  color: #64748b;
}

.ev-module-card__key {
  font-size: 11px;
  color: #94a3b8;
  font-family: 'SF Mono', 'Fira Code', monospace;
}

.ev-module-card--inactive .ev-module-card__key {
  color: #94a3b8;
}

.ev-module-card__status {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding-top: 2px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ev-module-card__status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

/* Pricing */
.ev-module-card__pricing {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 12px 0;
  border-top: 1px solid #f1f5f9;
  margin-bottom: 6px;
}

.ev-module-card--inactive .ev-module-card__pricing {
  border-top-color: #e2e8f0;
}

.ev-module-card__price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.ev-module-card__price-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #94a3b8;
}

.ev-module-card__price-value {
  font-size: 13px;
  font-weight: 700;
}

.ev-module-card__price-value--muted {
  color: #475569;
}

/* Actions */
.ev-module-card__actions {
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ev-module-card--inactive .ev-module-card__actions {
  border-top-color: #e2e8f0;
}

.ev-module-card__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  padding: 6px 4px;
  cursor: pointer;
}

.ev-module-card__action-btn--danger {
  color: #ef4444;
}

.ev-module-card__action-btn--activate {
  color: #059669;
}

/* ============================================================
   Confirm dialog — the base .confirm-card styles are global (see
   TheModalConfirmation.vue). Only the green "Activate" variant is new.
   The scoped attribute survives v-dialog teleporting the node to <body>.
   ============================================================ */
.confirm-card__btn--activate {
  background: #059669;
  color: #fff;
  border: none;
}

.confirm-card__btn--activate:hover {
  background: #047857;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

.confirm-card__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

/* ============================================================
   Responsive — mobile (<=599px)
   ============================================================ */
@media (max-width: 959px) {
  /* The counts sit under the title once the header row can no longer hold
     title + stats + button on one line. */
  .ev-modules-head__info {
    flex-basis: calc(100% - 54px);
  }
}

@media (max-width: 599px) {
  .ev-modules-page {
    padding: 20px 14px 40px !important;
  }
  .ev-modules-head {
    padding: 16px;
    gap: 12px;
    margin-bottom: 14px;
  }
  .ev-new-btn {
    flex: 1;
    justify-content: center;
  }
  .ev-toolbar {
    padding: 12px 14px;
  }
  .ev-modules-card__body {
    padding: 14px;
  }
  .ev-search {
    max-width: none;
    min-width: 0;
    flex-basis: 100%;
  }
  /* Single column — the 300px min track overflows a phone otherwise. */
  .ev-modules-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
}
</style>

<!--
  Non-scoped styles targeting the Vuetify dialog wrapper. v-dialog teleports
  itself to <body> outside the component tree, so scoped rules cannot reach
  it. Match the 16px radius of .em-form inside so the scrollbar clips
  cleanly against the rounded corner instead of overflowing past it.
-->
<style>
.v-dialog.ev-dialog {
  border-radius: 16px;
  overflow: hidden;
}
.v-dialog.ev-dialog > .em-form {
  border-radius: 0;
}

/* The dialog opens at 80vw on desktop. On smaller screens the form needs
   more room, so widen it — but it stays a centered, rounded dialog with
   margins (not full-screen). !important is required because Vuetify writes
   width/max-width inline. */
@media (max-width: 959px) {
  .v-dialog.ev-dialog {
    width: 94vw !important;
    max-width: 94vw !important;
  }
}
@media (max-width: 599px) {
  .v-dialog.ev-dialog {
    width: 96vw !important;
    max-width: 96vw !important;
  }
}
</style>
